/**
 * Client-side mirror of the backend's recurrence rule expansion.
 *
 * Only used to preview what a rule will produce before it is submitted; the
 * server expands the rule again and is the authority on what gets written.
 */

import type { RecurrenceFrequency } from "@/types";

export interface RecurrenceFormState {
  enabled: boolean;
  frequency: RecurrenceFrequency;
  weekday: number;
  weekOfMonth: number;
  endDate: string;
}

export const MAX_OCCURRENCES = 500;

export const WEEKDAY_OPTIONS = [
  { value: "0", label: "Maanantai" },
  { value: "1", label: "Tiistai" },
  { value: "2", label: "Keskiviikko" },
  { value: "3", label: "Torstai" },
  { value: "4", label: "Perjantai" },
  { value: "5", label: "Lauantai" },
  { value: "6", label: "Sunnuntai" },
];

export const WEEK_OF_MONTH_OPTIONS = [
  { value: "1", label: "Ensimmäinen" },
  { value: "2", label: "Toinen" },
  { value: "3", label: "Kolmas" },
  { value: "4", label: "Neljäs" },
  { value: "-1", label: "Viimeinen" },
];

export const FREQUENCY_OPTIONS = [
  { value: "DAILY_WEEKDAYS", label: "Joka arkipäivä" },
  { value: "WEEKLY", label: "Viikoittain" },
  { value: "MONTHLY_NTH_WEEKDAY", label: "Kuukausittain" },
];

export const emptyRecurrence = (): RecurrenceFormState => ({
  enabled: false,
  frequency: "WEEKLY",
  weekday: 0,
  weekOfMonth: 1,
  endDate: "",
});

/** Local-time `"YYYY-MM-DD"` for today. */
export function todayYmd(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

// Javascript weeks start on Sunday; everything here numbers them from Monday,
// matching the backend's Python weekday().
const mondayIndex = (d: Date) => (d.getDay() + 6) % 7;

function nthWeekdayOfMonth(
  year: number,
  month: number,
  weekday: number,
  nth: number,
): Date | null {
  if (nth === -1) {
    const last = new Date(year, month + 1, 0);
    last.setDate(last.getDate() - ((mondayIndex(last) - weekday + 7) % 7));
    return last;
  }
  const first = new Date(year, month, 1);
  const day = new Date(first);
  day.setDate(1 + ((weekday - mondayIndex(first) + 7) % 7) + (nth - 1) * 7);
  return day.getMonth() === month ? day : null;
}

/** Every date the rule produces between the two bounds, both inclusive. */
export function occurrenceDates(
  state: RecurrenceFormState,
  startYmd: string,
): Date[] {
  if (!state.endDate || !startYmd) return [];
  const start = new Date(`${startYmd}T00:00:00`);
  const end = new Date(`${state.endDate}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
  if (end < start) return [];

  const dates: Date[] = [];

  if (state.frequency === "DAILY_WEEKDAYS") {
    for (const d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      if (mondayIndex(d) < 5) dates.push(new Date(d));
      // Stop runaway loops if the range is absurd; the server enforces the
      // real cap and the UI refuses to submit past it anyway.
      if (dates.length > MAX_OCCURRENCES) break;
    }
    return dates;
  }

  if (state.frequency === "WEEKLY") {
    const first = new Date(start);
    first.setDate(
      first.getDate() + ((state.weekday - mondayIndex(start) + 7) % 7),
    );
    for (const d = new Date(first); d <= end; d.setDate(d.getDate() + 7)) {
      dates.push(new Date(d));
      if (dates.length > MAX_OCCURRENCES) break;
    }
    return dates;
  }

  let year = start.getFullYear();
  let month = start.getMonth();
  while (new Date(year, month, 1) <= end) {
    const day = nthWeekdayOfMonth(year, month, state.weekday, state.weekOfMonth);
    if (day && day >= start && day <= end) dates.push(day);
    if (dates.length > MAX_OCCURRENCES) break;
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }
  return dates;
}

const fiDate = (d: Date) =>
  `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;

/** Preview line shown under the recurrence controls. */
export function describeRecurrence(
  state: RecurrenceFormState,
  startYmd: string,
): string | null {
  const dates = occurrenceDates(state, startYmd);
  if (dates.length === 0) return null;
  return `Luodaan ${dates.length} tehtävää välillä ${fiDate(dates[0])}–${fiDate(
    dates[dates.length - 1],
  )}.`;
}
